import { useState } from 'react';
import { useAction } from 'convex/react';
import { api } from '../convex/_generated/api';
import { Id } from '../convex/_generated/dataModel';
import { useAuth } from '../lib/auth/AuthContext';

export interface TranslationResult {
  success: boolean;
  updatedComponents: number;
  errors: string[];
  message: string;
}

export interface UseTranslationOptions {
  onSuccess?: (result: TranslationResult) => void;
  onError?: (error: string) => void;
}

export const useTranslation = (options?: UseTranslationOptions) => {
  const [isTranslating, setIsTranslating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<TranslationResult | null>(null);
  
  const translateProject = useAction(api.translation.translateProject);
  const { sessionToken } = useAuth();

  const translate = async (projectId: Id<"projects">, targetLanguage: string) => {
    if (!sessionToken) {
      const errorMsg = "User not authenticated";
      setError(errorMsg);
      options?.onError?.(errorMsg);
      return;
    }

    setIsTranslating(true);
    setError(null);
    setResult(null);

    try {
      const translationResult = await translateProject({
        projectId,
        targetLanguage,
        sessionToken,
      });

      setResult(translationResult);

      if (translationResult.success) {
        options?.onSuccess?.(translationResult);
      } else {
        const errorMsg = translationResult.message || "Translation failed";
        setError(errorMsg);
        options?.onError?.(errorMsg);
      }

      return translationResult;
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : "Translation failed";
      setError(errorMsg);
      options?.onError?.(errorMsg);
      throw err;
    } finally {
      setIsTranslating(false);
    }
  };

  const reset = () => {
    setError(null);
    setResult(null);
    setIsTranslating(false);
  };

  return {
    translate,
    isTranslating,
    error,
    result,
    reset,
  };
}; 